import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { Group } from '@/types';
import { Check, Plus, Trash2, Settings, Users2, Shield, Search } from 'lucide-react';
import { useGroup } from '@/store/group';
import { useUsers } from '@/store/user';
import { useRoles } from '@/store/role';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { TabBar } from '@/components/ui/TabBar';

export const ManageGroupPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const groupStore = useGroup(id);
    const allUsers = useUsers((state) => state.users);
    const listAllUsers = useUsers((state) => state.list);
    const allRoles = useRoles((state) => state.roles);
    const listAllRoles = useRoles((state) => state.list);

    const tabs = [
        { id: 'Details', label: 'Details', icon: Settings },
        { id: 'Users', label: 'Members', icon: Users2 },
        { id: 'Roles', label: 'Roles', icon: Shield }
    ];
    const [activeTab, setActiveTab] = useState<string>('Details');

    const [group, setGroup] = useState<Group | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [groupUsers, setGroupUsers] = useState<any[]>([]);
    const [groupRoles, setGroupRoles] = useState<string[]>([]);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState<'users' | 'roles' | null>(null);
    const [modalValue, setModalValue] = useState<string>('');
    const [userSearch, setUserSearch] = useState('');
    const [isSavingModal, setIsSavingModal] = useState(false);

    // Confirm-remove member state
    const [confirmRemoveMember, setConfirmRemoveMember] = useState<any | null>(null);
    const [groupRoleNames, setGroupRoleNames] = useState<string[]>([]);

    useEffect(() => {
        if (!id) return;
        Promise.all([
            groupStore.get(),
            listAllUsers(),
            listAllRoles(),
            groupStore.roles.list(),
            groupStore.users.list(),
        ]).then(([g, , , roles, users]) => {
            setGroup(g);
            setBreadcrumbs([{ name: 'Groups', to: '/groups' }, { name: g.name || 'Manage Group' }]);
            setGroupRoles(roles);
            setGroupUsers(users);
            setGroupRoleNames(roles); // same for role name hints
        }).catch(console.error);
    }, [groupStore, id, listAllUsers, listAllRoles, setBreadcrumbs]);

    const submitDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!group || !id) return;
        try {
            const payload: any = {
                name: group.name,
                description: (group as any).description,
                owner_ids: group.owner_ids,
            };
            await groupStore.update(payload);
            toast.success('Group details saved');
            navigate('/groups');
        } catch (err) {
            console.error(err);
            setError('Failed to save');
        }
    };

    const addUser = async (userId: string) => {
        if (!id) return;
        setIsSavingModal(true);
        try {
            await groupStore.users.create(userId);
            const found = allUsers?.find((u) => u.id === userId);
            if (found) setGroupUsers((prev) => [...prev, found]);
            toast.success('Member added');
            setModalOpen(false);
        } catch {
            toast.error('Failed to add member');
        } finally {
            setIsSavingModal(false);
        }
    };

    const removeUser = async (userId: string) => {
        if (!id) return;
        try {
            await groupStore.users.delete(userId);
            setGroupUsers((prev) => prev.filter((u) => u.id !== userId));
            toast.success('Member removed');
            setConfirmRemoveMember(null);
        } catch {
            toast.error('Failed to remove member');
        }
    };

    const addRole = async (roleId: string) => {
        if (!id) return;
        setIsSavingModal(true);
        try {
            await groupStore.roles.create(roleId);
            setGroupRoles((prev) => [...prev, roleId]);
            toast.success('Role assigned');
            setModalOpen(false);
        } catch {
            toast.error('Failed to assign role');
        } finally {
            setIsSavingModal(false);
        }
    };

    const removeRole = async (roleId: string) => {
        if (!id) return;
        try {
            await groupStore.roles.delete(roleId);
            setGroupRoles((prev) => prev.filter((r) => r !== roleId));
            toast.success('Role removed');
        } catch {
            toast.error('Failed to remove role');
        }
    };

    if (!group) return <Loader />;

    const nonGroupUsers = allUsers?.filter((u) => !groupUsers.some((gu) => gu.id === u.id));
    const filteredUsers = userSearch
        ? nonGroupUsers.filter((u) =>
            u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.name?.toLowerCase().includes(userSearch.toLowerCase())
          )
        : nonGroupUsers;

    const unassignedRoles = allRoles.filter((r) => !groupRoles.includes(r.id));

    return (
        <PageLayout title={group.name || 'Manage Group'} description={group.email || id}>
            <TabBar
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                className="mb-6"
            />

            {activeTab === 'Details' && (
                <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6 md:p-8">
                    {error && (
                        <div className="p-4 mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium">
                            {error}
                        </div>
                    )}
                    <form onSubmit={submitDetails} className="space-y-6">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Name</label>
                                <input
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 w-full"
                                    value={group.name}
                                    onChange={(e) => setGroup({ ...group, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Description</label>
                                <input
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 w-full"
                                    value={(group as any).description || ''}
                                    onChange={(e) => setGroup({ ...group, description: e.target.value } as Group)}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Email</label>
                                <input
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 w-full"
                                    value={group.email || ''}
                                    onChange={(e) => setGroup({ ...group, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Owners</label>
                                <select
                                    multiple
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white w-full"
                                    value={group.owner_ids || []}
                                    onChange={(e) => {
                                        const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                                        setGroup({ ...group, owner_ids: opts });
                                    }}
                                >
                                    {allUsers?.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <button
                                type="submit"
                                className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm flex items-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'Users' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <button
                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm"
                            onClick={() => {
                                setModalType('users');
                                setModalValue('');
                                setUserSearch('');
                                setModalOpen(true);
                            }}
                        >
                            <Plus className="w-4 h-4" />
                            Add Member
                        </button>
                    </div>
                    <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                        {groupUsers.length === 0 ? (
                            <div className="p-12 text-center flex flex-col items-center justify-center">
                                <Users2 className="w-10 h-10 text-slate-300 dark:text-slate-600 mb-3" />
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">No members in this group.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-slate-50/50 dark:bg-slate-900/50 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                            <th className="px-6 py-3">Member</th>
                                            <th className="px-6 py-3">Email</th>
                                            <th className="px-6 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                        {groupUsers.map((u) => (
                                            <tr key={u.id} className="hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors group">
                                                <td className="px-6 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold text-xs">
                                                            {u.name?.charAt(0).toUpperCase() || u.email?.charAt(0).toUpperCase() || '?'}
                                                        </div>
                                                        <span className="block text-sm font-bold text-slate-900 dark:text-white">{u.name || 'Unnamed'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <span className="text-sm text-slate-500 dark:text-slate-400">{u.email}</span>
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <button
                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
                                                        onClick={() => setConfirmRemoveMember(u)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'Roles' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <button
                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm"
                            onClick={() => {
                                setModalType('roles');
                                setModalValue('');
                                setModalOpen(true);
                            }}
                        >
                            <Plus className="w-4 h-4" />
                            Assign Role
                        </button>
                    </div>
                    <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                        {groupRoles.length === 0 ? (
                            <div className="p-12 text-center flex flex-col items-center justify-center">
                                <Shield className="w-10 h-10 text-slate-300 dark:text-slate-600 mb-3" />
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">No roles assigned to this group.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-slate-50/50 dark:bg-slate-900/50 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                                            <th className="px-6 py-3">Role</th>
                                            <th className="px-6 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
                                        {groupRoles.map((r) => {
                                            const roleObj = allRoles.find((role) => role.id === r);
                                            return (
                                                <tr key={r} className="hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors group">
                                                    <td className="px-6 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <Shield className="w-4 h-4 text-orange-500 shrink-0" />
                                                            <div>
                                                                <span className="block text-sm font-bold text-slate-900 dark:text-white font-mono">{r}</span>
                                                                {roleObj?.name && roleObj.name !== r && (
                                                                    <span className="text-xs text-slate-400">{roleObj.name}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3 text-right">
                                                        <button
                                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
                                                            onClick={() => removeRole(r)}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal for add operations */}
            {modalOpen && modalType && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                            {modalType === 'users' ? 'Add Member' : 'Assign Role'}
                        </h2>

                        {modalType === 'users' && (
                            <div className="space-y-3 mb-6">
                                {/* Searchable user list */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                                    />
                                </div>
                                <div className="max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-900">
                                    {filteredUsers.length === 0 ? (
                                        <p className="px-4 py-6 text-center text-sm text-slate-400">No users available</p>
                                    ) : (
                                        filteredUsers.map((u) => (
                                            <button
                                                key={u.id}
                                                onClick={() => setModalValue(u.id)}
                                                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors ${modalValue === u.id ? 'bg-orange-50 dark:bg-orange-500/10' : ''}`}
                                            >
                                                <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">
                                                    {u.name?.charAt(0).toUpperCase() || u.email?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="block text-sm font-bold text-slate-900 dark:text-white truncate">{u.name || 'Unnamed'}</span>
                                                    <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                                                </div>
                                                {modalValue === u.id && (
                                                    <span className="ml-auto shrink-0 w-2 h-2 rounded-full bg-orange-500" />
                                                )}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {modalType === 'roles' && (
                            <div className="space-y-2 mb-6">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Select Role</label>
                                <select
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white w-full"
                                    value={modalValue}
                                    onChange={(e) => setModalValue(e.target.value)}
                                >
                                    <option value="" disabled>Choose a role...</option>
                                    {unassignedRoles.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name} ({r.id})</option>
                                    ))}
                                </select>
                                {unassignedRoles.length === 0 && (
                                    <p className="text-xs text-slate-400 italic">All available roles are already assigned.</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <button
                                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                onClick={() => setModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                                disabled={!modalValue || isSavingModal}
                                onClick={async () => {
                                    if (modalType === 'users') await addUser(modalValue);
                                    if (modalType === 'roles') await addRole(modalValue);
                                }}
                            >
                                {isSavingModal ? (
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : null}
                                Assign
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm-remove member modal */}
            {confirmRemoveMember && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-950 border border-red-200 dark:border-red-900/40 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white">Remove Member</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Removing <span className="font-bold text-slate-700 dark:text-slate-300">{confirmRemoveMember.name || confirmRemoveMember.email}</span> from this group.
                                </p>
                            </div>
                        </div>

                        {groupRoleNames.length > 0 && (
                            <div className="mb-5">
                                <h4 className="text-[11px] font-bold text-red-400 uppercase tracking-widest mb-2">
                                    Inherited Roles That Will Be Lost
                                </h4>
                                <div className="border border-red-100 dark:border-red-900/30 rounded-xl p-3 bg-red-50/30 dark:bg-red-950/20 divide-y divide-red-100/50 dark:divide-red-900/20">
                                    {groupRoleNames.map(r => {
                                        const roleObj = allRoles.find(role => role.id === r);
                                        return (
                                            <div key={r} className="py-1.5 text-xs font-mono text-red-600 dark:text-red-400 first:pt-0 last:pb-0">
                                                {r}{roleObj?.name && roleObj.name !== r ? ` (${roleObj.name})` : ''}
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-slate-400 mt-2 italic">
                                    This user will lose all access granted through this group's role assignments.
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <button
                                className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                onClick={() => setConfirmRemoveMember(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-bold transition-colors"
                                onClick={() => removeUser(confirmRemoveMember.id)}
                            >
                                Remove Member
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
};
